/**
 * Campos de atributos do prato (#214): dietas, picância, tags de sabor,
 * infantil e prato da casa. Partilhado pelo editor do produto (ProductModal)
 * e pela tela «Revisar atributos» (#216) — os dois ecrãs têm exatamente os
 * mesmos campos e data-testids.
 */
import { useState } from 'react'
import { DIET_TAG_OPTIONS, dietTagLabel, toggleTag } from '../lib/attributes.js'
import type { ProductAttributesBody } from '../lib/api.js'
import { Button } from './ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

export type AttributeSelection = Required<ProductAttributesBody>

/** Botões de picância 0–3 (sem picância … 🌶🌶🌶). */
function SpicePicker({
  value,
  onChange,
  testIdPrefix,
}: {
  value: number
  onChange: (level: number) => void
  testIdPrefix: string
}) {
  const { t } = useI18n()
  return (
    <div className="flex flex-wrap gap-2" role="group" data-testid={`${testIdPrefix}spice`}>
      {[0, 1, 2, 3].map((level) => (
        <button
          key={level}
          type="button"
          data-testid={`${testIdPrefix}spice-${level}`}
          aria-pressed={value === level}
          onClick={() => onChange(level)}
          className={cn(
            'rounded-full border px-3 py-1 text-xs transition-colors',
            value === level
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
          )}
        >
          {t(`admin.menu.spice_${level}`)}
        </button>
      ))}
    </div>
  )
}

/**
 * O conjunto completo de campos de atributos. `selection` é controlado pelo
 * chamador (estado local do editor/revisão); `testIdPrefix` distingue os
 * ecrãs (catalog-… / review-…).
 */
export function AttributeFields({
  selection,
  onChange,
  testIdPrefix,
}: {
  selection: AttributeSelection
  onChange: (next: AttributeSelection) => void
  testIdPrefix: string
}) {
  const { t, lang } = useI18n()
  const [customTag, setCustomTag] = useState('')

  const addCustomTag = () => {
    const clean = customTag.trim().toLowerCase()
    if (clean.length < 2) return
    if (!selection.flavor_tags.includes(clean)) {
      onChange({ ...selection, flavor_tags: [...selection.flavor_tags, clean].slice(0, 6) })
    }
    setCustomTag('')
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}attributes-fields`}>
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">{t('admin.menu.attributes_diet')}</p>
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}diet-tags`}>
          {DIET_TAG_OPTIONS.map((tag) => {
            const on = selection.diet_tags.includes(tag)
            return (
              <button
                key={tag}
                type="button"
                data-testid={`${testIdPrefix}diet-${tag}`}
                aria-pressed={on}
                onClick={() =>
                  onChange({ ...selection, diet_tags: toggleTag(selection.diet_tags, tag) })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  on
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
                )}
              >
                {dietTagLabel(t, tag, lang)}
              </button>
            )
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">{t('admin.menu.attributes_spice')}</p>
        <SpicePicker
          value={selection.spice_level}
          onChange={(spice_level) => onChange({ ...selection, spice_level })}
          testIdPrefix={testIdPrefix}
        />
      </div>

      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">
          {t('admin.menu.attributes_flavor')}
        </p>
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid={`${testIdPrefix}flavor-tags`}
        >
          {selection.flavor_tags.map((tag) => (
            <button
              key={tag}
              type="button"
              data-testid={`${testIdPrefix}flavor-${tag}`}
              aria-pressed={true}
              onClick={() =>
                onChange({
                  ...selection,
                  flavor_tags: selection.flavor_tags.filter((f) => f !== tag),
                })
              }
              className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-600 hover:border-red-300 hover:text-red-600"
              title={t('admin.common.delete')}
            >
              {tag} ×
            </button>
          ))}
          <span className="inline-flex items-center gap-1">
            <input
              data-testid={`${testIdPrefix}flavor-input`}
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addCustomTag()
                }
              }}
              className="w-28 rounded-md border border-gray-200 px-2 py-1 text-xs"
              placeholder={t('admin.menu.attributes_flavor_ph')}
              maxLength={24}
            />
            <Button
              variant="secondary"
              size="sm"
              data-testid={`${testIdPrefix}flavor-add`}
              onClick={addCustomTag}
              disabled={selection.flavor_tags.length >= 6}
            >
              +
            </Button>
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}is-kids`}
            checked={selection.is_kids}
            onChange={(e) => onChange({ ...selection, is_kids: e.target.checked })}
            className="w-4 h-4 rounded"
          />
          <span className="text-xs text-gray-700">{t('admin.menu.attributes_kids')}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`${testIdPrefix}is-signature`}
            checked={selection.is_signature}
            onChange={(e) => onChange({ ...selection, is_signature: e.target.checked })}
            className="w-4 h-4 rounded"
          />
          <span className="text-xs text-gray-700">{t('admin.menu.attributes_signature')}</span>
        </label>
      </div>
    </div>
  )
}
